import { 
    Group, 
    Text, 
    Button, 
    FileInput, 
    Image, 
    Card, 
    Stack, 
    ActionIcon, 
    Alert,
} from '@mantine/core';
import { 
    IconUpload,
    IconX, 
    IconDownload, 
    IconEye, 
    IconFile,
    IconPhoto,
    IconFileText
} from '@tabler/icons-react';
import React, { useState, useRef } from 'react';
import { notifications } from '@mantine/notifications';

interface QuestionMediaUploadProps {
    value?: File | null;
    existingMedia?: {
        mediaPath: string;
        originalFilename: string;
        contentType: string;
        size: number;
    };
    onChange: (file: File | null) => void;
    onRemove?: () => void;
    questionId?: string;
    disabled?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif'
];

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'];

export default function QuestionMediaUpload({
    value,
    existingMedia,
    onChange,
    onRemove,
    questionId,
    disabled = false
}: QuestionMediaUploadProps) {
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLButtonElement>(null);

    const handleFileSelect = (file: File | null) => {
        if (!file) {
            onChange(null);
            return;
        }

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            notifications.show({
                title: 'Invalid File Type',
                message: `File type not supported. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`,
                color: 'red',
            });
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            notifications.show({
                title: 'File Too Large',
                message: `File size exceeds maximum allowed size of 10MB`,
                color: 'red',
            });
            return;
        }

        onChange(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (contentType: string) => {
        if (contentType.startsWith('image/')) {
            return <IconPhoto size={20} />;
        } else if (contentType === 'application/pdf') {
            return <IconFileText size={20} />;
        } else {
            return <IconFile size={20} />;
        }
    };

    const isImage = (contentType: string) => {
        return contentType.startsWith('image/');
    };

    const getMediaUrl = () => {
        if (existingMedia?.mediaPath) {
            // Use the correct URL format for question media
            return `${import.meta.env.VITE_API_URL}/files/view-application-file/${existingMedia.mediaPath}`;
        }
        return null;
    };

    return (
        <Stack gap="md">
            {/* File Upload Area - Only show if no file is selected and no existing media */}
            {!existingMedia && !value && (
                <Card 
                    withBorder 
                    p="md" 
                    style={{
                        border: dragOver ? '2px dashed #228be6' : '1px solid #dee2e6',
                        backgroundColor: dragOver ? '#f8f9fa' : 'white',
                        transition: 'all 0.2s ease'
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <Stack gap="sm" align="center">
                        <IconUpload size={40} color="#6c757d" />
                        <Text size="sm" c="dimmed" ta="center">
                            Drag and drop an image here, or click to select
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                            Supported formats: JPG, PNG, GIF (Max 10MB)
                        </Text>
                        
                        <FileInput
                            ref={fileInputRef}
                            accept={ALLOWED_EXTENSIONS.join(',')}
                            value={value}
                            onChange={handleFileSelect}
                            disabled={disabled}
                            style={{ display: 'none' }}
                        />
                        
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={disabled}
                        >
                            Select Image
                        </Button>
                    </Stack>
                </Card>
            )}

            {/* Selected File Preview - Compact version */}
            {value && (
                <Card withBorder p="sm">
                    <Group justify="space-between" align="center">
                        <Group gap="sm">
                            <IconPhoto size={20} color="#228be6" />
                            <div>
                                <Text size="sm" fw={500} lineClamp={1}>{value.name}</Text>
                                <Text size="xs" c="dimmed">{formatFileSize(value.size)}</Text>
                            </div>
                        </Group>
                        <Group gap="xs">
                            <ActionIcon
                                color="red"
                                variant="subtle"
                                onClick={() => handleFileSelect(null)}
                                disabled={disabled}
                                title="Remove image"
                            >
                                <IconX size={16} />
                            </ActionIcon>
                        </Group>
                    </Group>
                    
                    <Image
                        src={URL.createObjectURL(value)}
                        alt="Preview"
                        style={{ maxHeight: 150, objectFit: 'contain', marginTop: '8px' }}
                    />
                </Card>
            )}

            {/* Existing Media Display - Compact version */}
            {existingMedia && (
                <Card withBorder p="sm">
                    <Group justify="space-between" align="center">
                        <Group gap="sm">
                            <IconPhoto size={20} color="#228be6" />
                            <div>
                                <Text size="sm" fw={500} lineClamp={1}>{existingMedia.originalFilename}</Text>
                                <Text size="xs" c="dimmed">{formatFileSize(existingMedia.size)}</Text>
                            </div>
                        </Group>
                        <Group gap="xs">
                            {getMediaUrl() && (
                                <ActionIcon
                                    variant="subtle"
                                    onClick={() => window.open(getMediaUrl()!, '_blank')}
                                    title="View image"
                                >
                                    <IconEye size={16} />
                                </ActionIcon>
                            )}
                            <ActionIcon
                                variant="subtle"
                                onClick={() => {
                                    if (questionId) {
                                        import('@/services/questions').then(({ downloadQuestionMedia }) => {
                                            downloadQuestionMedia(questionId, existingMedia.originalFilename);
                                        });
                                    }
                                }}
                                title="Download image"
                            >
                                <IconDownload size={16} />
                            </ActionIcon>
                            {onRemove && (
                                <ActionIcon
                                    color="red"
                                    variant="subtle"
                                    onClick={onRemove}
                                    disabled={disabled}
                                    title="Remove image"
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            )}
                        </Group>
                    </Group>
                    
                    {getMediaUrl() && (
                        <Image
                            src={getMediaUrl()!}
                            alt="Question Media"
                            style={{ maxHeight: 150, objectFit: 'contain', marginTop: '8px' }}
                        />
                    )}
                </Card>
            )}

            {/* Validation Alert */}
            {value && !ALLOWED_TYPES.includes(value.type) && (
                <Alert color="red" icon={<IconX size={16} />}>
                    <Text size="sm">
                        Invalid file type. Please select a supported file format.
                    </Text>
                </Alert>
            )}

            {value && value.size > MAX_FILE_SIZE && (
                <Alert color="red" icon={<IconX size={16} />}>
                    <Text size="sm">
                        File size exceeds maximum allowed size of 10MB.
                    </Text>
                </Alert>
            )}
        </Stack>
    );
}
